import {
    PocDivider,
    PocIcon,
    PocInput,
    PocList,
    PocListItem,
    PocTypography,
} from 'commons/components/components-wrapper';
import { OwcListItemCustomEvent } from '@one/web-components/dist/types/components';
import { HighlightedText } from 'commons/components/highlighted-text';
import { useTranslation } from 'core/i18n';
import { MAX_NUMBER_OF_OPTIONS_WITHOUT_SEARCH } from 'modules/devices/domain';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { mapperFilterParentWithChildren } from './searchable-list.mapper';
import { SearchableListItem } from './searchable-list.model';
import { SearchableListContainer, SearchableListContent } from './searchable-list.style';

interface SearchableListProps {
    items: SearchableListItem[];
    fixedItems?: SearchableListItem[];
    // Uncontrolled mode
    defaultSelectedIds?: (string | number)[];
    // Controlled mode
    selectedIds?: (string | number)[];
    multiple?: boolean;
    showParentSelectorControl?: boolean;
    messageNoItems?: string;
    onSelectItem?: (ids: (string | number)[]) => void;
    onFilterList?: (val: { ids: (string | number)[]; filterTermValue: string }) => void;
}

export const SearchableList: React.FC<SearchableListProps> = ({
    items,
    fixedItems = [],
    multiple = false,
    defaultSelectedIds,
    selectedIds,
    showParentSelectorControl = undefined,
    messageNoItems,
    onSelectItem,
    onFilterList,
}) => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState<string>('');
    const [expandedItems, setExpandedItems] = useState<Set<string | number>>(() => new Set());

    const isControlled = selectedIds !== undefined;
    const [internalSelectedItems, setInternalSelectedItems] = useState<(string | number)[]>(defaultSelectedIds ?? []);

    const currentSelectedItems = isControlled ? selectedIds : internalSelectedItems;
    // O(1) membership per row instead of O(selected) — the tree can be long.
    const selectedSet = useMemo(() => new Set(currentSelectedItems), [currentSelectedItems]);

    const handleSelectItem = (ids: (string | number)[]) => {
        if (!isControlled) {
            setInternalSelectedItems(ids);
        }
        onSelectItem?.(ids);
    };

    const handleToggleExpanded = useCallback((itemId: string | number, isExpanded: boolean) => {
        setExpandedItems((previousExpandedItems) => {
            const nextExpandedItems = new Set(previousExpandedItems);
            if (isExpanded) {
                nextExpandedItems.add(itemId);
            } else {
                nextExpandedItems.delete(itemId);
            }
            return nextExpandedItems;
        });
    }, []);

    const filteredItems = useMemo(() => mapperFilterParentWithChildren(items, filter), [items, filter]);
    const filteredFixedItems = useMemo(() => mapperFilterParentWithChildren(fixedItems, filter), [fixedItems, filter]);

    useEffect(() => {
        if (onFilterList) {
            const ids = filteredItems.flatMap((item) => [item.id, ...(item.children?.map((child) => child.id) ?? [])]);
            onFilterList({ ids, filterTermValue: filter });
        }
    }, [filteredItems, filter, onFilterList]);

    const hasNotItems =
        items.length + fixedItems.length === 0 || filteredItems.length + filteredFixedItems.length === 0;

    const renderItem = (option: SearchableListItem) => (
        <PocListItem
            key={option.id}
            value={option.id}
            showSelectorControl={option.children == null}
            expanded={expandedItems.has(option.id)}
            onExpandedChange={(event: OwcListItemCustomEvent<boolean>) =>
                handleToggleExpanded(option.id, event.detail === true)
            }
            disableRipple
            noBorder
            selected={
                option.children == null
                    ? selectedSet.has(option.id)
                    : option.children.some((child) => selectedSet.has(child.id))
            }
            data-testid={`searchable-list-item-${option.id}`}
        >
            <span>
                <HighlightedText value={filter} text={option.name} />
            </span>
            {option.children != null && (
                <PocList slot="children">
                    {option.children?.map((child) => (
                        <PocListItem
                            value={child.id}
                            key={child.id}
                            noBorder
                            showSelectorControl
                            selected={selectedSet.has(child.id)}
                            data-testid={`searchable-list-item-${child.id}`}
                        >
                            <HighlightedText value={filter} text={child.name} />
                        </PocListItem>
                    ))}
                </PocList>
            )}
        </PocListItem>
    );

    return (
        <SearchableListContainer>
            {items.length + fixedItems.length > MAX_NUMBER_OF_OPTIONS_WITHOUT_SEARCH && (
                <PocInput
                    placeholder={t('commons.components.searchable-list.search.placeholder')}
                    className="search-button"
                    variant="outlined"
                    value={filter}
                    onValueChange={(ev) => setFilter(ev.detail)}
                >
                    <PocIcon slot="prefix" name="search" family="outlined" />
                </PocInput>
            )}
            <SearchableListContent>
                {hasNotItems ? <PocTypography variant="subtitle1">{messageNoItems}</PocTypography> : null}
                <PocList
                    data-testid="searchable-list"
                    multiple={multiple}
                    // POC-22651: owc-list scroll-jumps to the pre-click selection when it self-focuses.
                    notFocusable
                    selected={currentSelectedItems}
                    onSelectedChange={(ev) => handleSelectItem(ev.detail)}
                    {...(showParentSelectorControl === undefined
                        ? {}
                        : { showSelectorControl: showParentSelectorControl })}
                >
                    {filteredFixedItems.map(renderItem)}
                    {filteredFixedItems.length > 0 && filteredItems.length > 0 && (
                        <PocDivider data-testid="searchable-list-fixed-divider" />
                    )}
                    {filteredItems.map(renderItem)}
                </PocList>
            </SearchableListContent>
        </SearchableListContainer>
    );
};
